import { propertyFetch } from '../services/idxPropertyClient';
import { loadPropertyMedia, propertyMediaUrls } from '../services/propertyMedia';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import { useRef, useState } from 'react';
import { SITE_CONFIG } from '../config';

const usePropertyDetail = () => {
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [propertyDetailImages, setPropertyDetailImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [propertyMedia, setPropertyMedia] = useState([]);
  const detailRequestRef = useRef(0);

  const fetchAllPropertyImages = async (listingKey, requestId = detailRequestRef.current, cover = '') => {
    try {
      const media = await loadPropertyMedia(listingKey);
      const images = propertyMediaUrls(media);
      if (requestId !== detailRequestRef.current) return;
      setPropertyMedia(media);
      if (images.length > 0) {
          const firstThree = images.slice(0, 3);
          setPropertyDetailImages(firstThree);
          setImagesLoading(false);
          rememberPropertyPhoto(listingKey, images[0]);
          if (images.length > 3) {
            setTimeout(() => {
              if (requestId === detailRequestRef.current) setPropertyDetailImages(images);
            }, 500);
          }
      } else {
        if (cover) setPropertyDetailImages([cover]);
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching all property images:', error);
      if (requestId === detailRequestRef.current) {
        if (cover) setPropertyDetailImages([cover]);
        setImagesLoading(false);
      }
    }
  };

  const fetchPropertyDetail = async (propertyId, summaryData) => {
    const requestId = ++detailRequestRef.current;
    setPropertyMedia([]);
    const cover = summaryData?.first_photo_url || summaryData?.image_url || summaryData?.photo_url;
    setPropertyDetail(summaryData || null);
    setPropertyDetailImages(cover ? [cover] : []);
    setImagesLoading(!cover);
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data/${propertyId}`, {});
      if (requestId !== detailRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        const property = data.properties[0];
        setPropertyDetail({
          ...property,
          created_at: property.created_at || summaryData?.created_at,
          updated_at: property.updated_at || summaryData?.updated_at
        });
        if (property.listing_key) {
          fetchAllPropertyImages(property.listing_key, requestId, cover);
        }
      } else {
        console.error('Failed to fetch property details');
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      if (requestId === detailRequestRef.current) setImagesLoading(false);
    }
  };

  const fetchPrivatePropertyDetail = async (listing) => {
    setPropertyMedia([]);
    setPropertyDetailImages([]);
    setImagesLoading(true);
    const listingId = String(listing.listing_key).replace('PRIVATE_', '');

    let fullListing = listing;
    if (!listing.current_price && !listing.address) {
      try {
        const detailResponse = await fetch(`https://tophamtechbe.com/api/treas/private/v1/listing/${listingId}?site_id=${SITE_CONFIG.SITE_ID}`, {
          headers: { 'x-api-key': 'treas-api-key-2025' }
        });
        if (detailResponse.ok) {
          const detailData = await detailResponse.json();
          const d = detailData.listing || detailData;
          fullListing = {
            ...d,
            listing_key: `PRIVATE_${d.id}`,
            current_price: d.listing_type === 'sale' ? d.price_sale : d.price_lease_monthly,
            bedrooms_total: d.beds,
            address: `${d.address_line_1}, ${d.city}, ${d.state_province}`,
            isPrivate: true
          };
        }
      } catch (error) {
        console.error('Error fetching private listing detail:', error);
      }
    }

    try {
      const mediaResponse = await fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${SITE_CONFIG.SITE_ID}/${listingId}`, {
        headers: { 'x-api-key': 'treas-api-key-2025' }
      });
      if (mediaResponse.ok) {
        const mediaData = await mediaResponse.json();
        const sorted = (mediaData.media || []).sort((a, b) => a.order_hint - b.order_hint);
        setPropertyMedia(sorted);
        setPropertyDetailImages(sorted.map(img => img.media_url || img.s3_link));
      }
    } catch (error) {
      console.error('Error fetching private property media:', error);
    } finally {
      setImagesLoading(false);
    }

    const address = fullListing.address || '';
    setPropertyDetail({
      ...fullListing,
      listing_key: fullListing.listing_key,
      current_price: fullListing.current_price,
      bedrooms_total: fullListing.bedrooms_total,
      virtual_tour_url: fullListing.virtual_tour_url,
      created_at: fullListing.created_at,
      updated_at: fullListing.updated_at,
      location: {
        street_number: address.split(',')[0] || '',
        city: fullListing.city || '',
        state: fullListing.state_province || '',
        postal_code: fullListing.postal_code || ''
      },
      features: {
        public_remarks: fullListing.description || ''
      },
      isPrivate: true
    });
  };

  const fetchPropertyDetailByListingKey = async (listingKey) => {
    if (String(listingKey).startsWith('PRIVATE_')) {
      fetchPrivatePropertyDetail({ listing_key: listingKey });
      return;
    }
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?listingKey=${listingKey}`, {});
      if (response.ok) {
        const data = await response.json();
        const properties = data.properties || [];
        if (properties.length > 0) {
          const property = properties[0];
          if (property.id) {
            fetchPropertyDetail(property.id, property);
          }
        } else {
          console.error('No property found for listing key:', listingKey);
          alert('Property not found. Please check the listing key.');
        }
      } else {
        console.error('Failed to fetch property by listing key');
        alert('Failed to load property. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching property by listing key:', error);
      alert('Failed to load property. Please try again.');
    }
  };

  // Like fetchPropertyDetail, but does NOT wipe the already-shown image(s) first — used
  // for the optimistic open below so the summary photo stays visible while detail loads.
  const enrichPropertyDetail = async (propertyId, summaryData, requestId) => {
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data/${propertyId}`, {});
      if (requestId !== detailRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        const property = data.properties[0];
        setPropertyDetail({
          ...property,
          created_at: property.created_at || summaryData?.created_at,
          updated_at: property.updated_at || summaryData?.updated_at
        });
        if (property.listing_key) {
          const cover = summaryData?.first_photo_url || summaryData?.image_url || summaryData?.photo_url;
          fetchAllPropertyImages(property.listing_key, requestId, cover);
        }
      }
    } catch (error) {
      console.error('Error enriching property details:', error);
    }
  };

  // Snappy open straight from the summary the tile already has: paint the modal
  // immediately (price, address, first photo), then enrich with full detail + all
  // photos in the background. Skips the redundant summary-by-listingKey round-trip.
  const openPropertyFromSummary = (summary) => {
    if (!summary) return;
    if (String(summary.listing_key).startsWith('PRIVATE_')) {
      fetchPrivatePropertyDetail(summary);
      return;
    }
    const requestId = ++detailRequestRef.current;
    setPropertyMedia([]);
    const cover = summary.first_photo_url || summary.image_url || summary.photo_url;
    setPropertyDetail({
      ...summary,
      location: {
        street_number: summary.street_number,
        street_dir_prefix: summary.street_dir_prefix,
        street_suffix: summary.street_suffix,
        street_dir_suffix: summary.street_dir_suffix,
        unit_number: summary.unit_number,
        street_name: summary.street_name,
        city: summary.city,
        state: summary.state,
        postal_code: summary.postal_code
      },
      features: summary.features || {}
    });
    setPropertyDetailImages(cover ? [cover] : []);
    setImagesLoading(!cover);
    if (summary.id) {
      enrichPropertyDetail(summary.id, summary, requestId);
    } else {
      fetchPropertyDetailByListingKey(summary.listing_key);
    }
  };

  const closePropertyDetail = () => {
    detailRequestRef.current += 1;
    setPropertyDetail(null);
    setImagesLoading(false);
  };

  return {
    propertyDetail,
    setPropertyDetail,
    closePropertyDetail,
    propertyDetailImages,
    propertyMedia,
    imagesLoading,
    fetchPropertyDetail,
    fetchPropertyDetailByListingKey,
    fetchPrivatePropertyDetail,
    openPropertyFromSummary
  };
};

export default usePropertyDetail;
